import { useMemo } from "react";
import { Box, Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import _ from "lodash";
import { FormProvider, RHFMultipleSelectField, RHFTextField } from "../../common/hook-form";
import { FieldLabel, FilterField, FilterDateField } from "../../common/FilterBar";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import DateRangePicker from "../DateRangePicker";
import { SEVERITY_OPTIONS } from "./severity";
import { PLATFORM_OPTIONS } from "./platform";
import { CARD_SX, COLORS } from "./emsTheme";

// Prozo standard look (Analytics): 12px labels, 13px inputs, purple Filter, neutral Reset.
const THEMED_SX = {
    ...CARD_SX, mb: 2,
    "& label, & .MuiTypography-root": { fontSize: "12px !important", letterSpacing: ".04em" },
    "& .MuiInputBase-root, & .MuiInputBase-input, & .MuiSelect-select, & input::placeholder": { fontSize: "13px !important" },
};

/** The Date Range / Merchant / Courier / Ticket ID / AWB / Category / Subcategory / Status /
 *  Assignee filter bar (every pick-list a multi-select with Select all / Clear: any of the picked values) shared by the Tickets and Analytics pages — same fields (mostly), same behavior,
 *  so the two pages read as one section rather than two independently-built ones. Takes the
 *  return value of useEscalationFilterBar plus the page's own submit/reset handlers (which
 *  additionally need to reset that page's own state, e.g. pagination — not something this bar
 *  owns).
 *
 *  hideFields lets a page drop fields that don't apply to it — Ticket ID/AWB narrow to a single
 *  ticket, which is meaningful on the Tickets list but not on an aggregate Analytics view (it'd
 *  just reduce every stat to 0-or-1 ticket's worth), so AnalyticsPage passes
 *  hideFields={['ticketId', 'awb']}. Date Range/Merchant/Courier/Category/Subcategory/Status all
 *  narrow an aggregate meaningfully too, so those stay on both pages. */
const EscalationFilterBar = ({ bar, couriers, onSubmit, onReset, hideFields = [], themed = true }) => {
    const { filterMethods, dateRange, setDateRange, categories, statusList, subcategoryOptions, assigneeOptions, orderStatusOptions } = bar;
    const isHidden = (field) => hideFields.includes(field);
    // ~1000 couriers: mapped once per list, not on every render of the page around the bar.
    const courierOptions = useMemo(() => couriers.map((val) => ({ value: val.cpAccountCode, label: val.cpAccountCode })), [couriers]);
    const categoryOptions = useMemo(() => _.orderBy(categories, "categoryName").map((c) => ({ value: c.categoryName, label: c.categoryName })), [categories]);
    const statusOptions = useMemo(() => statusList.map((s) => ({ value: s.label, label: s.label })), [statusList]);

    return (
        <Box sx={themed ? THEMED_SX : { mb: 2, backgroundColor: '#fff', borderRadius: 2, border: '1px solid #e0e0e0', p: 2 }}>
            <FormProvider methods={filterMethods} onSubmit={filterMethods.handleSubmit(onSubmit)}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>
                    <FilterDateField>
                        <FieldLabel>Date Range</FieldLabel>
                        <DateRangePicker
                            defaultRange={dateRange}
                            maxDate={new Date()}
                            showTextBox
                            label=""
                            placeholder="Select Date Range"
                            size="small"
                            onChange={(range) => setDateRange(range)}
                        />
                    </FilterDateField>

                    <FilterField>
                        <FieldLabel>Merchant</FieldLabel>
                        <MerchantFilterCached fullWidth multiple selectAll />
                    </FilterField>

                    <FilterField>
                        <FieldLabel>Courier</FieldLabel>
                        {/* Courier account codes — pick several, e.g. every Delhivery account. */}
                        <RHFMultipleSelectField
                            name="courier" label="" placeholder="Select Courier" size="small" width="100%" search selectAll
                            options={courierOptions}
                        />
                    </FilterField>

                    {!isHidden('ticketId') && (
                        <FilterField>
                            <FieldLabel>Ticket ID</FieldLabel>
                            <RHFTextField label="" name="ticketId" placeholder="ESC or Freshdesk no." fullWidth size="small" InputLabelProps={{ shrink: true }} />
                        </FilterField>
                    )}

                    {!isHidden('awb') && (
                        <FilterField>
                            <FieldLabel>AWB</FieldLabel>
                            <RHFTextField label="" name="awb" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                        </FilterField>
                    )}

                    <FilterField>
                        <FieldLabel>Category</FieldLabel>
                        <RHFMultipleSelectField
                            name="category" label="" placeholder="Select Category" size="small" width="100%" search selectAll
                            options={categoryOptions}
                        />
                    </FilterField>

                    <FilterField>
                        <FieldLabel>Subcategory</FieldLabel>
                        <RHFMultipleSelectField
                            name="subcategory" label="" placeholder="Select Subcategory" size="small" width="100%" search selectAll
                            options={subcategoryOptions}
                        />
                    </FilterField>

                    <FilterField>
                        <FieldLabel>Status</FieldLabel>
                        <RHFMultipleSelectField
                            name="status" label="" placeholder="Select Status" size="small" width="100%" search selectAll
                            options={statusOptions}
                        />
                    </FilterField>

                    {orderStatusOptions?.length > 0 && (
                        <FilterField sx={{ flex: '0 1 200px' }}>
                            <FieldLabel>Order Status</FieldLabel>
                            <RHFMultipleSelectField
                                name="orderStatus" label="" placeholder="Any" size="small" width="100%" search selectAll
                                options={orderStatusOptions}
                            />
                        </FilterField>
                    )}

                    <FilterField sx={{ flex: '0 1 150px' }}>
                        <FieldLabel>Severity</FieldLabel>
                        <RHFMultipleSelectField
                            name="severity" label="" placeholder="Any" size="small" width="100%" selectAll
                            options={SEVERITY_OPTIONS}
                        />
                    </FilterField>

                    {!isHidden('platform') && (
                        <FilterField sx={{ flex: '0 1 150px' }}>
                            <FieldLabel>Platform</FieldLabel>
                            <RHFMultipleSelectField
                                name="platform" label="" placeholder="Any" size="small" width="100%" selectAll
                                options={PLATFORM_OPTIONS}
                            />
                        </FilterField>
                    )}

                    {assigneeOptions && !isHidden('assignee') && (
                        // Fixed width: it often wraps onto a row of its own, where flex-grow
                        // would stretch it across the whole bar.
                        <FilterField sx={{ flex: '0 1 220px' }}>
                            <FieldLabel>Assignee</FieldLabel>
                            <RHFMultipleSelectField
                                name="assignee" label="" placeholder="Select Assignee" size="small" width="100%" search selectAll
                                options={assigneeOptions}
                            />
                        </FilterField>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, flex: '0 0 auto' }}>
                        {/* Solid #1565C0 / solid red — matches V1 EscalationManagement's Filter
                            and Reset buttons exactly (confirmed live against admin.proship.in),
                            not variant="contained"'s default theme primary (an unrelated blue,
                            #2065D1) and not an outlined Reset. */}
                        <LoadingButton
                            type="submit" variant="contained" size="small"
                            sx={themed
                                ? { textTransform: 'none', fontWeight: 600, fontSize: 14, bgcolor: COLORS.purple, boxShadow: 'none', '&:hover': { bgcolor: '#55337D', boxShadow: 'none' } }
                                : { textTransform: 'none', fontWeight: 700, bgcolor: '#1565C0', '&:hover': { bgcolor: '#0D47A1' } }}
                        >
                            Filter
                        </LoadingButton>
                        <Button
                            type="button" variant={themed ? 'outlined' : 'contained'} size="small" onClick={onReset}
                            sx={themed
                                ? { textTransform: 'none', fontWeight: 600, fontSize: 14, color: COLORS.ink2, borderColor: COLORS.grid, '&:hover': { borderColor: COLORS.ink3, bgcolor: COLORS.ground } }
                                : { textTransform: 'none', fontWeight: 700, bgcolor: '#EF4444', color: '#fff', '&:hover': { bgcolor: '#DC2626' } }}
                        >
                            Reset
                        </Button>
                    </Box>
                </Box>
            </FormProvider>
        </Box>
    );
};

export default EscalationFilterBar;
